/*
 * Project: Goalscape
 * File:    bookmarkUtils.js
 * Path:    src/utils/bookmarkUtils.js
 * Description: Utility functions for managing bookmarks in Firebase.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Ensure proper error handling and logging for all Firebase operations. Consider adding caching for frequently accessed bookmark data.
 */
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

/*
 * Function: initializeUserBookmarks
 * Description: Initializes the bookmarks collection for a new user in Firebase.
 * Parameters:
 *   userId: string - The ID of the user to initialize bookmarks for.
 * Returns: Promise<{items: any[], order: string[]}> - The initialized bookmark data.
 * Usage:
 *   const bookmarks = await initializeUserBookmarks('user123');
 * AI Hint: Consider using transactions for atomicity when initializing bookmarks.
 */
export const initializeUserBookmarks = async (userId) => {
    try {
        const userBookmarksRef = doc(db, 'bookmarks', userId);
        const bookmarksDoc = await getDoc(userBookmarksRef);

        if (!bookmarksDoc.exists()) {
            await setDoc(userBookmarksRef, {
                items: [],
                order: [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
            return { items: [], order: [] };
        }

        return bookmarksDoc.data();
    } catch (error) {
        console.error('Error initializing user bookmarks:', error);
        throw error;
    }
};

/*
 * Function: updateUserBookmarks
 * Description: Updates a user's bookmarks collection in Firebase.
 * Parameters:
 *   userId: string - The ID of the user whose bookmarks to update.
 *   bookmarks: any[] - The updated array of bookmarks.
 * Returns: Promise<void>
 * Usage:
 *   await updateUserBookmarks('user123', updatedBookmarks);
 * AI Hint: Implement pagination for large bookmark collections to improve performance.
 */
export const updateUserBookmarks = async (userId, bookmarks) => {
    try {
        const userBookmarksRef = doc(db, 'bookmarks', userId);
        await updateDoc(userBookmarksRef, {
            items: bookmarks,
            updatedAt: new Date().toISOString()
        });
    } catch (error) {
        console.error('Error updating user bookmarks:', error);
        throw error;
    }
};

/*
 * Function: addBookmark
 * Description: Adds a new bookmark to a user's collection.
 * Parameters:
 *   userId: string - The ID of the user to add the bookmark for.
 *   bookmarkData: object - The bookmark data to add.
 * Returns: Promise<object> - The newly created bookmark.
 * Usage:
 *   const newBookmark = await addBookmark('user123', { url: 'https://example.com' });
 * AI Hint: Consider adding server-side validation for bookmark URLs.
 */
export const addBookmark = async (userId, bookmarkData) => {
    try {
        if (!userId) {
            throw new Error('User ID is required');
        }

        const userBookmarksRef = doc(db, 'bookmarks', userId);
        const bookmarksDoc = await getDoc(userBookmarksRef);
        const currentData = bookmarksDoc.exists() ? bookmarksDoc.data() : { items: [], order: [] };

        if (!Array.isArray(currentData.items)) {
            currentData.items = [];
        }
        if (!Array.isArray(currentData.order)) {
            currentData.order = [];
        }

        const newBookmark = {
            id: Date.now().toString(),
            url: bookmarkData.url,
            ...bookmarkData,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        const updatedItems = [...currentData.items, newBookmark];
        const updatedOrder = [...currentData.order, newBookmark.id];

        await updateDoc(userBookmarksRef, {
            items: updatedItems,
            order: updatedOrder,
            updatedAt: new Date().toISOString()
        });

        return newBookmark;
    } catch (error) {
        console.error('Error adding bookmark:', error);
        throw error;
    }
};

/*
 * Function: removeBookmark
 * Description: Removes a bookmark from a user's collection.
 * Parameters:
 *   userId: string - The ID of the user to remove the bookmark from.
 *   bookmarkId: string - The ID of the bookmark to remove.
 * Returns: Promise<void>
 * Usage:
 *   await removeBookmark('user123', 'bookmark456');
 * AI Hint: Implement soft delete instead of permanent deletion for data recovery.
 */
export const removeBookmark = async (userId, bookmarkId) => {
    try {
        const userBookmarksRef = doc(db, 'bookmarks', userId);
        const bookmarksDoc = await getDoc(userBookmarksRef);

        if (!bookmarksDoc.exists()) {
            throw new Error('User bookmarks not found');
        }

        const currentData = bookmarksDoc.data();
        const updatedItems = currentData.items.filter(item => item.id !== bookmarkId);
        const updatedOrder = currentData.order.filter(id => id !== bookmarkId);

        await updateDoc(userBookmarksRef, {
            items: updatedItems,
            order: updatedOrder,
            updatedAt: new Date().toISOString()
        });
    } catch (error) {
        console.error('Error removing bookmark:', error);
        throw error;
    }
};

/*
 * Function: updateBookmark
 * Description: Updates an existing bookmark in a user's collection.
 * Parameters:
 *   userId: string - The ID of the user whose bookmark to update.
 *   bookmarkId: string - The ID of the bookmark to update.
 *   updates: object - The updates to apply to the bookmark.
 * Returns: Promise<object> - The updated bookmark.
 * Usage:
 *   const updatedBookmark = await updateBookmark('user123', 'bookmark456', { title: 'New Title' });
 * AI Hint: Implement optimistic updates to improve UI responsiveness.
 */
export const updateBookmark = async (userId, bookmarkId, updates) => {
    try {
        const userBookmarksRef = doc(db, 'bookmarks', userId);
        const bookmarksDoc = await getDoc(userBookmarksRef);

        if (!bookmarksDoc.exists()) {
            throw new Error('User bookmarks not found');
        }

        const currentData = bookmarksDoc.data();
        const updatedItems = currentData.items.map(item => {
            if (item.id === bookmarkId) {
                return {
                    ...item,
                    ...updates,
                    updatedAt: new Date().toISOString()
                };
            }
            return item;
        });

        await updateDoc(userBookmarksRef, {
            items: updatedItems,
            updatedAt: new Date().toISOString()
        });

        return updatedItems.find(item => item.id === bookmarkId);
    } catch (error) {
        console.error('Error updating bookmark:', error);
        throw error;
    }
};

/*
 * Function: updateBookmarkOrder
 * Description: Updates the order of bookmarks in a user's collection.
 * Parameters:
 *   userId: string - The ID of the user whose bookmark order to update.
 *   newOrder: string[] - Array of bookmark IDs in their new order.
 * Returns: Promise<void>
 * Usage:
 *   await updateBookmarkOrder('user123', ['bookmark1', 'bookmark2', 'bookmark3']);
 * AI Hint: Consider using a more efficient data structure for storing bookmark order (e.g., linked list).
 */
export const updateBookmarkOrder = async (userId, newOrder) => {
    try {
        const userBookmarksRef = doc(db, 'bookmarks', userId);
        await updateDoc(userBookmarksRef, {
            order: newOrder,
            updatedAt: new Date().toISOString()
        });
    } catch (error) {
        console.error('Error updating bookmark order:', error);
        throw error;
    }
};

/*
 * Function: fetchUrlMetadata
 * Description: Fetches metadata (title and preview image) for a given URL.
 * Parameters:
 *   url: string - The URL to fetch metadata for.
 * Returns: Promise<{title: string, previewImage: string | null}> - The metadata.
 * Usage:
 *   const metadata = await fetchUrlMetadata('https://example.com');
 * AI Hint: Implement caching for fetched URL metadata to reduce API calls. Use a service like metascraper for more reliable metadata extraction.
 */
export const fetchUrlMetadata = async (url) => {
    try {
        console.log('Fetching URL metadata from webhook:', url);
        const response = await fetch(`https://n8n.akmaldju.com/webhook/fetch-url?url=${encodeURIComponent(url)}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            timeout: 5000
        });

        console.log('Webhook response:', response);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data && data.title) {
            return {
                title: data.title,
                previewImage: null,
                content: data.content ? data.content : null
            };
        } else {
            throw new Error('Title not found in webhook response');
        }
    } catch (error) {
        console.error('Error fetching URL metadata from webhook:', error);
        try {
            const urlObj = new URL(url);
            const title = urlObj.hostname || url;
            return {
                title,
                previewImage: null,
                content: null
            };
        } catch (fallbackError) {
            console.error('Error fetching URL metadata (fallback):', fallbackError);
            return {
                title: url,
                previewImage: null,
                content: null
            };
        }
    }
};
